import {ControllerFlowAPI} from '@wix/yoshi-flow-editor'
import {Experience} from '@wix/ambassador-table-reservations-v1-experience/types'
import {TimeSlot, Status} from '@wix/ambassador-table-reservations-v1-time-slot/types'
import {ReservationLocation} from '@wix/ambassador-table-reservations-v1-reservation-location/types'
import {Details} from '@wix/ambassador-table-reservations-v1-reservation/types'
import {
  IWidgetControllerConfig,
  SiteStructure,
} from '@wix/native-components-infra/dist/src/types/types'
import {BreadcrumbsItem} from 'wix-ui-tpa/dist/cssVars/types/components'
import {ResolvedError} from '@wix/yoshi-flow-editor/error-handler'

import {noop} from '../../utils/helpers'
import {RequestStatus, wrapRequest} from '../../utils/wrapRequest'
import {createStorageProvider} from '../../utils/storageContext'
import {experiencesService} from '../../services/experiencesService'
import {getIsSiteBusinessPremium} from '../../services/premiumFeaturesService'
import {reservationLocationsService} from '../../services/reservationLocationsService'
import {timeSlotsService} from '../../services/timeSlotsService'
import {getReservationLocationsMock} from '../../editor/editorMocks/getReservationLocationsMock'
import {getTimeSlotsMock} from '../../editor/editorMocks/getTimeSlotsMock'
import {getDayTimeSlotsMock} from '../../editor/editorMocks/getDayTimeSlotsMock'
import {getBreadcrumbsConfig} from '../../utils/getBreadcrumbsConfig'
import {AppPage} from '../../utils/constants'
import {getRegionalSettings, Instance} from '../../utils/flowAPI'
import {TimeSlotsByDate} from '../../types/TimeSlots'
import {
  getExperienceBySlugMock,
  getExperienceMock,
} from '../../editor/editorMocks/getExperienceMock'

import {handleReservationData} from './actions/handleReservationData'

interface ExperienceDetailsData {
  regionalSettings?: string
  msid: string
  getReservationLocationsStatus: RequestStatus
  getExperienceStatus: RequestStatus
  getIsSiteBusinessPremiumStatus: RequestStatus
  getTimeSlotsStatus: RequestStatus
  getDayTimeSlotsStatus: RequestStatus
  handleReservationDataStatus: RequestStatus
  handleReservationDataError?: ResolvedError
  isSiteBusinessPremium: boolean
  breadcrumbsConfig: BreadcrumbsItem[]
  reservationLocations: ReservationLocation[]
  experienceId: string | undefined
  slug: string | undefined
  experience: Experience | undefined
  dayTimeSlots: TimeSlot[]
  timeSlots: TimeSlotsByDate
  fitToContentHeight: boolean
}

interface ExperienceDetailsActions {
  getReservationLocations: () => Promise<ReservationLocation[] | undefined>
  getIsSiteBusinessPremium: () => Promise<boolean | undefined>
  getExperience: (experienceId: string) => Promise<Experience | undefined>
  getExperienceBySlug: (slug: string) => Promise<Experience | undefined>
  getDayTimeSlots: (params: {
    reservationLocationId: string
    partySize: number
    date: Date
    timeSlotInterval?: number | null
    timeZone?: string | null
    experienceId?: string
  }) => Promise<TimeSlot[] | undefined>
  getTimeSlots: (params: {
    reservationLocationId: string
    partySize: number
    date: Date
    timeSlotInterval?: number | null
    statuses: Status[]
    timeZone?: string | null
    experienceId?: string
  }) => Promise<TimeSlotsByDate | undefined>
  handleReservationData: (params: {
    details: Details
    timeZone: string | null
    experienceId: string
    isManualApproval: boolean
  }) => Promise<void>
}

export interface ExperienceDetailsStorage extends ExperienceDetailsData, ExperienceDetailsActions {}

const defaultExperienceDetailsStorage: ExperienceDetailsStorage = {
  getReservationLocationsStatus: RequestStatus.DEFAULT,
  getExperienceStatus: RequestStatus.DEFAULT,
  getIsSiteBusinessPremiumStatus: RequestStatus.DEFAULT,
  getTimeSlotsStatus: RequestStatus.DEFAULT,
  getDayTimeSlotsStatus: RequestStatus.DEFAULT,
  handleReservationDataStatus: RequestStatus.DEFAULT,
  handleReservationDataError: undefined,
  msid: '',
  isSiteBusinessPremium: false,
  breadcrumbsConfig: [],
  reservationLocations: [],
  experienceId: undefined,
  slug: undefined,
  experience: undefined,
  dayTimeSlots: [],
  timeSlots: {},
  fitToContentHeight: true,
  getReservationLocations: noop,
  getIsSiteBusinessPremium: noop,
  getExperience: noop,
  getExperienceBySlug: noop,
  getDayTimeSlots: noop,
  getTimeSlots: noop,
  handleReservationData: noop,
}

const {withStorageProvider, useStorage} = createStorageProvider(defaultExperienceDetailsStorage)

const initExperienceDetailsStorage = (
  flowAPI: ControllerFlowAPI,
  controllerConfig: IWidgetControllerConfig,
  siteStructure: SiteStructure,
  instance: Instance,
): ExperienceDetailsStorage => {
  const query = flowAPI.controllerConfig?.wixCodeApi?.location?.query ?? {}
  const path = flowAPI.controllerConfig?.wixCodeApi?.location?.path
  const slug = Array.isArray(path) && path.length >= 2 ? path[path.length - 1] : undefined

  return {
    ...defaultExperienceDetailsStorage,
    msid: instance.metaSiteId!,
    breadcrumbsConfig: getBreadcrumbsConfig(flowAPI, siteStructure, AppPage.ExperiencesList),
    experienceId: query.experienceId,
    slug,
    regionalSettings: getRegionalSettings(flowAPI),
    getReservationLocations: wrapRequest(
      flowAPI,
      reservationLocationsService.getReservationLocations,
      'reservationLocations',
      'getReservationLocationsStatus',
    ),
    getIsSiteBusinessPremium: wrapRequest(
      flowAPI,
      getIsSiteBusinessPremium,
      'isSiteBusinessPremium',
      'getIsSiteBusinessPremiumStatus',
    ),
    getExperience: wrapRequest(
      flowAPI,
      experiencesService.getExperience,
      'experience',
      'getExperienceStatus',
    ),
    getExperienceBySlug: wrapRequest(
      flowAPI,
      experiencesService.getExperienceBySlug,
      'experience',
      'getExperienceStatus',
    ),
    getDayTimeSlots: wrapRequest(
      flowAPI,
      timeSlotsService.getDayTimeSlots,
      'dayTimeSlots',
      'getDayTimeSlotsStatus',
    ),
    getTimeSlots: wrapRequest(
      flowAPI,
      timeSlotsService.getTimeSlots,
      'timeSlots',
      'getTimeSlotsStatus',
    ),
    handleReservationData: handleReservationData(flowAPI),
  }
}

const mockExperienceDetailsStorage = (
  flowAPI: ControllerFlowAPI,
  controllerConfig: IWidgetControllerConfig,
  siteStructure: SiteStructure,
  instance: Instance,
): ExperienceDetailsStorage => {
  const initiatedStorage = initExperienceDetailsStorage(
    flowAPI,
    controllerConfig,
    siteStructure,
    instance,
  )

  return {
    ...initiatedStorage,
    getReservationLocations: wrapRequest(
      flowAPI,
      getReservationLocationsMock,
      'reservationLocations',
      'getReservationLocationsStatus',
    ),
    getDayTimeSlots: wrapRequest(
      flowAPI,
      getDayTimeSlotsMock,
      'dayTimeSlots',
      'getDayTimeSlotsStatus',
    ),
    getTimeSlots: wrapRequest(flowAPI, getTimeSlotsMock, 'timeSlots', 'getTimeSlotsStatus'),
    getExperience: wrapRequest(flowAPI, getExperienceMock, 'experience', 'getExperienceStatus'),
    getExperienceBySlug: wrapRequest(
      flowAPI,
      getExperienceBySlugMock,
      'experience',
      'getExperienceStatus',
    ),
  }
}

export {
  initExperienceDetailsStorage,
  mockExperienceDetailsStorage,
  withStorageProvider as withExperienceDetailsStorageProvider,
  useStorage as useExperienceDetailsStorage,
}
